import React from "react";
import {useNavigate} from "react-router-dom";
import {Carousel, CarouselGameCard} from "@/components";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import styles from "./CustomBoardCarousel.module.scss";

export const CustomBoardCarousel = ({board}) => {
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);

  const [carouselApi, setCarouselApi] = React.useState(null);

  const items = React.useMemo(() => {
    if (!board?.items?.length) return [];
    return [...board.items]
      .sort((a, b) => a.position - b.position)
      .map(item => {
        if (!item.game) return null;
        const meta = getGameById(item.game.provider_game_id, item.game.provider?.name);
        return {
          itemId: item.id,
          // NEVER fall back to item.game.id for navigation: that's our internal
          // game_dbs UUID, not the provider's own game code, and the game-init
          // route expects the latter (provider_game_id). Only icon/name may
          // fall back to the raw board item.
          id: item.game.provider_game_id,
          name: meta?.name || item.game.name,
          icon: meta?.icon || item.game.custom_icon || item.game.icon,
          provider: meta?.provider || item.game.provider?.name,
          providerLabel: meta?.provider_display_name || item.game.provider?.display_name,
          position: item.position,
        };
      })
      .filter(Boolean);
  }, [board?.items, getGameById]);

  const handleGameClick = React.useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  if (!board?.items?.length) return null;

  const isLoading = gamesLoading || !gamesInitialized;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.titleBox}>
            <p className={styles.title}>{board.title}</p>
          </div>
          <div className={styles.arrowGroup}>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => carouselApi?.scrollPrev()}
              aria-label="Anterior"
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => carouselApi?.scrollNext()}
              aria-label="Siguiente"
            >
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
            </button>
          </div>
        </div>
        {board.subtitle && <p className={styles.subtitle}>{board.subtitle}</p>}
      </div>
      <Carousel
        autoScroll={false}
        onInit={setCarouselApi}
        renderItems={items.map(game => (
          <div key={game.itemId}>
            <CarouselGameCard
              loading={isLoading}
              name={game.name}
              icon={game.icon}
              providerLabel={game.providerLabel}
              onClick={() => handleGameClick(game.provider, game.id)}
            />
          </div>
        ))}
      />
    </div>
  );
};

export default CustomBoardCarousel;
